<template>
  <v-dialog v-model="dialog" max-width="900" persistent scrollable>
    <v-card>
      <v-card-title class="headline primary white--text">
        <v-icon left dark>mdi-history</v-icon>
        Change History
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4" style="max-height: 600px; overflow-y: auto;">
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p class="mt-2 grey--text">Loading history...</p>
        </div>

        <div v-else-if="auditLogs.length === 0" class="text-center py-8">
          <v-icon large color="grey lighten-1">mdi-history</v-icon>
          <p class="mt-2 grey--text">No history available</p>
        </div>

        <v-timeline v-else dense>
          <v-timeline-item
            v-for="log in auditLogs"
            :key="log.id"
            :color="getActionColor(log.action)"
            small
            fill-dot
          >
            <template v-slot:icon>
              <v-icon dark small>{{ getActionIcon(log.action) }}</v-icon>
            </template>

            <v-card outlined class="mb-2 elevation-1">
              <v-card-title class="py-2 px-3">
                <v-chip :color="getActionColor(log.action)" small dark class="mr-2">
                  {{ log.actionText }}
                </v-chip>
                <span class="body-2">by {{ log.userName }}</span>
                <v-spacer></v-spacer>
                <span class="caption grey--text">
                  {{ formatDateTime(log.createdAt) }}
                </span>
              </v-card-title>

              <v-card-text
                v-if="log.changedFields && Object.keys(log.changedFields).length > 0"
                class="py-2"
              >
                <v-expansion-panels flat accordion>
                  <v-expansion-panel>
                    <v-expansion-panel-header class="py-1">
                      <span class="body-2">
                        <v-icon small class="mr-1">mdi-pencil</v-icon>
                        {{
                          log.changeSummary ||
                            `${Object.keys(log.changedFields).length} field(s) changed`
                        }}
                      </span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-simple-table dense>
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left" style="width: 30%">Field</th>
                              <th class="text-left" style="width: 35%">Old Value</th>
                              <th class="text-left" style="width: 35%">New Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            <template v-for="(change, fieldName) in log.changedFields">
                              <tr v-if="isSimpleChange(change)" :key="fieldName">
                                <td class="font-weight-medium">{{ fieldName }}</td>
                                <td>
                                  <span
                                    class="red--text text--lighten-1"
                                    :class="{ 'text-decoration-line-through': change.newValue }"
                                  >
                                    {{ formatValue(change.oldValue) || "(empty)" }}
                                  </span>
                                </td>
                                <td>
                                  <span class="green--text text--darken-2">
                                    {{ formatValue(change.newValue) || "(empty)" }}
                                  </span>
                                </td>
                              </tr>
                              <tr v-else :key="fieldName + '-collection'">
                                <td class="font-weight-medium">{{ fieldName }}</td>
                                <td colspan="2">
                                  <v-chip
                                    v-if="change.added && change.added.length"
                                    x-small
                                    color="success"
                                    class="mr-1"
                                  >
                                    +{{ change.added.length }} added
                                  </v-chip>
                                  <v-chip
                                    v-if="change.removed && change.removed.length"
                                    x-small
                                    color="error"
                                    class="mr-1"
                                  >
                                    -{{ change.removed.length }} removed
                                  </v-chip>
                                  <v-chip
                                    v-if="change.modified && change.modified.length"
                                    x-small
                                    color="warning"
                                    class="mr-1"
                                  >
                                    {{ change.modified.length }} modified
                                  </v-chip>
                                </td>
                              </tr>
                            </template>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card-text>

              <v-card-text v-else class="py-2">
                <span class="body-2 grey--text">
                  <v-icon small class="mr-1">mdi-information-outline</v-icon>
                  {{ log.action === "CREATE" ? "Record created" : "Record deleted" }}
                </span>
              </v-card-text>
            </v-card>
          </v-timeline-item>
        </v-timeline>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-3">
        <span class="caption grey--text"> {{ auditLogs.length }} revision(s) </span>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="close">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "dialog-audit-history",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    entityType: {
      type: String,
      required: true,
    },
    entityId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    ...mapState("audit", ["entityHistory"]),
    auditLogs() {
      return this.entityHistory || [];
    },
  },
  watch: {
    dialog(val) {
      if (val && this.entityId) {
        this.fetchHistory();
      }
    },
  },
  methods: {
    async fetchHistory() {
      this.loading = true;
      try {
        await this.$store.dispatch("audit/getEntityHistory", {
          entityType: this.entityType,
          entityId: this.entityId,
        });
      } catch (error) {
        this.$store.dispatch("toast", {
          type: "error",
          message: "Failed to load audit history",
        });
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.$store.dispatch("audit/clearEntityHistory");
      this.$emit("close");
    },
    getActionColor(action) {
      const colors = {
        CREATE: "success",
        UPDATE: "warning",
        DELETE: "error",
      };
      return colors[action] || "grey";
    },
    getActionIcon(action) {
      const icons = {
        CREATE: "mdi-plus",
        UPDATE: "mdi-pencil",
        DELETE: "mdi-delete",
      };
      return icons[action] || "mdi-information";
    },
    formatDateTime(dateTime) {
      if (!dateTime) return "";
      const date = new Date(dateTime);
      return date.toLocaleString("id-ID", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatValue(value) {
      if (value === null || value === undefined) return "";
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
    },
    isSimpleChange(change) {
      return change && (change.oldValue !== undefined || change.newValue !== undefined);
    },
  },
};
</script>

<style scoped>
.v-timeline-item {
  padding-bottom: 8px;
}
</style>
